import React, { createContext, useState } from 'react';
import { Router } from '@uhg-abyss/web/ui/Router';

import { Monitor } from '@src/components/Monitor';
import { DetailView } from '@src/components/Reports/DetailView/DetailView';
import { MultiLevelView } from '@src/components/Reports/MultiLevelView/MultiLevelView';
import { GPSDetailView } from '@src/components/Reports/GPSDetailView/GPSDetailView';
import { ErrorPage } from '@src/common/Error';
import { CustomError } from '@src/common/Error/CustomError';
import { Logout } from '@src/common/Logout';
import { ToastProvider } from '@uhg-abyss/web/ui/Toast';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Button } from '@uhg-abyss/web/ui/Button';
import { UserObj, useAuth } from '@src/Providers/AuthProvider';
import CustomerProfile from '@src/components/CustomerProfile';
import { HelpMain } from '@src/components/HelpPages/HelpMain';
import RoleAdmin from '@src/components/Administration/RoleAdmin';
import Announcement from '@src/components/Administration/Announcement';
import { Reports } from '../components/Reports';
import { Home } from '../components/Home';
import { WlmHome } from '../components/Home/WlmHome';
import { AppBody } from '../common/AppBody';
import AddProfile from '@src/components/CustomerProfile/AddProfile';
import MoveProfile from '@src/components/CustomerProfile/MoveProfile/MoveProfile';
import CopyProfile from '@src/components/CustomerProfile/CopyProfile/CopyProfile';

export const FileContext = createContext<any>({
  fileData: {
    allDate: 'false',
  },
  setFileData: () => {},
});

const Authentication = () => {
  const [selectedUser, setSelectedUser] = useState('');
  const { userMap, userAccessCall } = useAuth();

  const handleEESUser = async () => {
    const user: UserObj | undefined = userMap?.find((x) => {
      return x.userId === selectedUser;
    });
    userAccessCall(user);
  };

  return userMap && userMap?.length > 1 ? (
    <Box
      width="500px"
      css={{
        'abyss-box-root': {
          margin: '20vh auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          alignItems: 'center',
        },
      }}
      color="#D9E9FA"
      padding="$web.semantic.spacing.scale.lg"
    >
      <SelectInput
        label="Select EES User"
        highlighted
        value={selectedUser}
        onChange={setSelectedUser}
        options={
          userMap?.map(({ userId, userType }) => {
            return {
              label: `${userId} (${userType})`,
              value: userId,
            };
          }) || []
        }
      />
      <Button onClick={handleEESUser} style={{ marginTop: '15px' }}>
        Submit
      </Button>
    </Box>
  ) : null;
};

export const Routes = () => {
  const [fileData, setFileData] = useState(null);
  const { isAuth } = useAuth();

  return (
    <FileContext.Provider value={{ fileData, setFileData }}>
      <ToastProvider />
      <Box
        color="transparent"
        padding={0}
        css={{
          'abyss-box-root': {
            margin: 0,
            '.abyss-data-table-expanded-sub-component-row': {
              width: '100%',
            },
            '.abyss-data-table-expanded-sub-component-cell': {
              width: '100%',
            },
          },
        }}
      >
        <Router.Routes title="{{title}} | CDB">
          <Router.Route path="/" element={<AppBody />}>
            <Router.Route
              path="/deactivated"
              element={
                <CustomError error="User ID is deactivated please reach out to EEMS team, " />
              }
            />
            <Router.Route
              path="/no-access"
              element={
                <CustomError error="You do not have access to any service, please reach out to EEMS team, " />
              }
            />
            <Router.Route
              path="/invalid"
              element={
                <CustomError error="User is unauthenticated please reach out to EES team, " />
              }
            />
            <Router.Route path="/error" element={<ErrorPage />} />
            <Router.Route path="/logout" element={<Logout />} />
            <Router.Route path="/ohcid" element={<Authentication />} />
            <Router.Route path="/eeseems" element={<Authentication />} />
          </Router.Route>
          {isAuth ? (
            <React.Fragment>
              <Router.Route path="/" element={<AppBody />}>
                <Router.Route index element={<Home />} />
                <Router.Route path="/home/roleAdmin" element={<RoleAdmin />} />
                <Router.Route
                  path="/home/announce"
                  element={<Announcement />}
                />
                <Router.Route
                  path="/:service?/home/reports/:setId/:reportType?/:fieldCode?/:errorCode?/:dataFieldValue?"
                  element={<Reports />}
                />
                <Router.Route
                  path="/:service?/home/child-reports/:fileId/:setId/:reportType?/:fieldCode?"
                  element={<Reports isChild />}
                />

                <Router.Route
                  path="/:service?/profile/:encryptedData"
                  element={<CustomerProfile />}
                />
                <Router.Route
                  path="/:service?/home/detail-view/:encryptedData"
                  element={<DetailView />}
                />

                <Router.Route
                  path="/:service?/home/gps-detail-view/:encryptedData"
                  element={<GPSDetailView />}
                />
                <Router.Route
                  path="/:service?/home/multi-level-view/:encryptedData"
                  element={<MultiLevelView />}
                />
                <Router.Route
                  path="/:service?/profile/search"
                  element={<CustomerProfile isSearch />}
                />
                <Router.Route
                  path="/:service?/profile/add"
                  element={<AddProfile />}
                />
                <Router.Route
                  path="/:service?/profile/move"
                  element={<MoveProfile />}
                />
                <Router.Route
                  path="/:service?/profile/copy"
                  element={<CopyProfile />}
                />
                <Router.Route path="/wlm" element={<WlmHome />} />
                <Router.Route path="/:service?" element={<Home />} />
              </Router.Route>
              <Router.Route
                path="/:service?/home/monitor/:fileId"
                element={<Monitor />}
              />
              <Router.Route
                path="/:service?/home/help/:reportType"
                element={<HelpMain />}
              />
            </React.Fragment>
          ) : null}
          <Router.Route
            path="/:service?/home/monitor/:fileId"
            element={<Monitor />}
          />

          <Router.Route path="*" element={<AppBody />}>
            <Router.Route path="*" element={<ErrorPage />} />
          </Router.Route>
        </Router.Routes>
      </Box>
    </FileContext.Provider>
  );
};
